
import React, { useEffect } from 'react';
import { HashRouter, Routes, Route, useLocation, Navigate } from 'react-router-dom';
import Navbar from './components/Navbar.tsx';
import Footer from './components/Footer.tsx';
import ScrollToTop from './components/ScrollToTop.tsx';
import Home from './pages/Home.tsx';
import ServiceDetail from './pages/ServiceDetail.tsx';
import Blog from './pages/Blog.tsx';
import BlogPostDetail from './pages/BlogPostDetail.tsx';
import Profile from './pages/Profile.tsx';
import CreatePost from './pages/CreatePost.tsx';
import EditPost from './pages/EditPost.tsx';
import AuthorProfile from './pages/AuthorProfile.tsx';
import Moderation from './pages/Moderation.tsx';
import { AppProvider, useApp } from './context/AppContext.tsx';

const ScrollToTopOnNavigate = () => {
  const { pathname, hash } = useLocation();
  
  useEffect(() => {
    if (hash) {
      const id = hash.replace('#', '');
      const element = document.getElementById(id);
      if (element) {
        const timer = setTimeout(() => {
          element.scrollIntoView({ behavior: 'smooth' });
        }, 150);
        return () => clearTimeout(timer);
      }
    } else {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, [pathname, hash]);

  return null;
};

const AppContent = () => {
  const { currentUser, hasPermission } = useApp();

  return (
    <div className="min-h-screen bg-slate-50 text-slate-900 selection:bg-indigo-100 selection:text-indigo-800 transition-colors duration-300">
      <Navbar />
      <main>
        <Routes>
          <Route path="/" element={currentUser ? <Navigate to="/blog" replace /> : <Home />} />
          <Route path="/service/:id" element={currentUser ? <Navigate to="/blog" replace /> : <ServiceDetail />} />
          <Route path="/blog" element={<Blog />} />
          <Route path="/blog/:id" element={<BlogPostDetail />} />
          <Route path="/author/:id" element={<AuthorProfile />} />
          <Route path="/profile" element={<Profile />} />
          <Route path="/moderation" element={hasPermission('CAN_APPROVE_POSTS') ? <Moderation /> : <Navigate to="/profile" replace />} />
          <Route path="/create-post" element={<CreatePost />} />
          <Route path="/edit-post/:id" element={<EditPost />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </main>
      
      <FooterLogic />
      
      <ScrollToTop />
      
      <div className="fixed top-0 left-0 w-full h-screen pointer-events-none -z-10 overflow-hidden">
        <div className="absolute top-[-10%] left-[-10%] w-[40%] h-[40%] bg-indigo-200/30 blur-[120px] rounded-full" />
        <div className="absolute bottom-[-10%] right-[-10%] w-[40%] h-[40%] bg-blue-200/30 blur-[120px] rounded-full" />
      </div>
    </div>
  );
};

const FooterLogic = () => {
  const { currentUser } = useApp();
  const location = useLocation();
  
  const isProfilePage = location.pathname === '/profile';
  const isModerationPage = location.pathname === '/moderation';
  const isEditorPage = location.pathname.startsWith('/create-post') || location.pathname.startsWith('/edit-post');
  
  if (currentUser && (isProfilePage || isModerationPage || isEditorPage)) return null;
  
  return <Footer />;
};

const App: React.FC = () => {
  return (
    <AppProvider>
      <HashRouter>
        <ScrollToTopOnNavigate />
        <AppContent />
      </HashRouter>
    </AppProvider>
  );
};

export default App;
